import { Text, UnstyledButton } from '@mantine/core';
import { Tooltip } from '~/components/MantineOverlays';
import clsx from 'clsx';
import { IconUser } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useViewerBrowsingLevelDebounced } from '~/components/BrowsingLevel/BrowsingLevelProvider';
import { EdgeMedia } from '~/components/EdgeMedia/EdgeMedia';
import { NamePlateText } from '~/components/User/NamePlateText';
import { UserAvatarProfilePicture } from '~/components/UserAvatar/UserAvatarProfilePicture';
import { useFeatureFlags } from '~/providers/FeatureFlagsProvider';
import type {
  BadgeCosmetic,
  ContentDecorationCosmetic,
  NamePlateCosmetic,
} from '~/server/selectors/cosmetic.selector';
import type { ProfileImage } from '~/server/selectors/image.selector';
import type { UserWithCosmetics } from '~/server/selectors/user.selector';
import {
  hasPublicBrowsingLevel,
  hasSafeBrowsingLevel,
} from '~/shared/constants/browsingLevel.constants';
import { Flags } from '~/shared/utils/flags';
import { getInitials } from '~/utils/string-helpers';
import classes from './UserAvatarSimple.module.scss';
import { decorationFrameStyle } from '~/components/UserAvatar/decoration-frame.util';
import { UserHoverCard } from '~/components/UserAvatar/UserHoverCard';
import { useBrowsingSettings } from '~/providers/BrowserSettingsProvider';
import { useCurrentUser } from '~/hooks/useCurrentUser';

export function UserAvatarSimple({
  id,
  profilePicture,
  username,
  deletedAt,
  cosmetics,
  autoplayAnimations,
  withHoverCard = true,
  classNames,
}: {
  id: number;
  profilePicture?: ProfileImage | null;
  username?: string | null;
  deletedAt?: Date | null;
  cosmetics?: UserWithCosmetics['cosmetics'] | null;
  autoplayAnimations?: boolean;
  withHoverCard?: boolean;
  /**
   * `avatar` goes on the avatar wrapper; `namePill` wraps the name in a backing element, and
   * `namePillPlate` is added to it when the user wears a nameplate.
   */
  classNames?: { avatar?: string; namePill?: string; namePillPlate?: string };
}) {
  const features = useFeatureFlags();
  const currentUser = useCurrentUser();
  // 🔴 The VIEWER's level, not the page's. A profile picture belongs to the
  // person in the avatar, not to whatever this page happens to be about — and
  // `ImageDetail2` sets a page override to the IMAGE's rating, so on a PG image
  // every commenter's PG-13 avatar was suppressed for viewers who accept PG-13.
  // Same defect as the remix gallery beside it (#4497); this is the other half.
  // Domain and policy caps still apply, only the page override is skipped.
  const browsingLevel = useViewerBrowsingLevelDebounced();
  const displayProfilePicture =
    !deletedAt && profilePicture && profilePicture.ingestion !== 'Blocked';
  const router = useRouter();
  const autoplayGifs = useBrowsingSettings((x) => x.autoplayGifs);

  if (id === -1) return null;

  const nameplate = cosmetics?.find(({ cosmetic }) =>
    cosmetic ? cosmetic.type === 'NamePlate' : undefined
  )?.cosmetic as Omit<NamePlateCosmetic, 'name' | 'description' | 'obtainedAt'>;
  const badge = cosmetics?.find(({ cosmetic }) =>
    cosmetic ? cosmetic.type === 'Badge' : undefined
  )?.cosmetic as Omit<BadgeCosmetic, 'description' | 'obtainedAt'>;
  const decoration = cosmetics?.find(({ cosmetic }) =>
    cosmetic ? cosmetic.type === 'ProfileDecoration' : undefined
  )?.cosmetic as Omit<ContentDecorationCosmetic, 'description' | 'obtainedAt'>;

  const name = (
    <NamePlateText
      size="sm"
      fw={500}
      lineClamp={1}
      color="white"
      className={classes.username}
      nameplate={nameplate?.data}
      autoplay={autoplayAnimations !== false}
    >
      {username}
    </NamePlateText>
  );

  const anim = !autoplayGifs || autoplayAnimations === false ? false : undefined;

  return (
    <UserHoverCard user={{ id, deletedAt }} disabled={!withHoverCard}>
      <UnstyledButton
        onClick={() => router.push(username ? `/user/${username}` : `/user?id=${id}`)}
        className={clsx('flex w-fit items-center gap-2', classNames?.namePill && 'max-w-full')}
      >
        {displayProfilePicture && (
          <div className={clsx('relative shrink-0', classNames?.avatar)}>
            <div className="flex size-8 items-center justify-center overflow-hidden rounded-full bg-white/30 dark:bg-black/30">
              {profilePicture &&
              (!features.canViewNsfw
                ? currentUser
                  ? hasSafeBrowsingLevel(profilePicture.nsfwLevel)
                  : hasPublicBrowsingLevel(profilePicture.nsfwLevel)
                : Flags.hasFlag(browsingLevel, profilePicture.nsfwLevel)) ? (
                <UserAvatarProfilePicture id={id} username={username} image={profilePicture} />
              ) : (
                <span className="text-sm font-semibold text-dark-8 dark:text-gray-0">
                  {username ? getInitials(username) : <IconUser size={32} />}
                </span>
              )}
            </div>

            {decoration && decoration.data.url && (
              <EdgeMedia
                src={decoration.data.url}
                anim={anim}
                // original={anim === false ? false : undefined}
                type="image"
                name="user avatar decoration"
                alt=""
                className="z-[2]"
                loading="lazy"
                style={decorationFrameStyle(decoration.data)}
                optimized
                width={96}
                original={false}
              />
            )}
          </div>
        )}
        {deletedAt ? (
          // Moderators get the id, which is otherwise unrecoverable from this row — see `Username`.
          <Text size="sm">{currentUser?.isModerator ? `[deleted] #${id}` : '[deleted]'}</Text>
        ) : (
          <>
            {classNames?.namePill ? (
              <div
                className={clsx(classNames.namePill, nameplate?.data && classNames.namePillPlate)}
              >
                {name}
              </div>
            ) : (
              name
            )}
            {badge?.data.url && (
              <Tooltip label={badge.name} withArrow withinPortal>
                <div className="shrink-0" style={{ display: 'flex', width: 28 }}>
                  <EdgeMedia
                    src={badge.data.url}
                    anim={badge.data.animated && anim}
                    original={false}
                    alt={badge.name}
                    optimized
                    loading="lazy"
                    width={96}
                  />
                </div>
              </Tooltip>
            )}
          </>
        )}
      </UnstyledButton>
    </UserHoverCard>
  );
}
